<template>
  <div>
    <report-panel-collapsible :report="report" :anchor="'keyfinancials'" :initallyOpen="true">
      <div slot="title">Key Financials</div>
      <div slot="body">
        <!-- panel content start -->
        <div class="table-responsive">
          <table class="table table-striped table-condensed" id="key-financials-detail-table">
            <thead>
              <tr>
                <th>&nbsp;</th>
                <th
                  class="text-right"
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :class="extraClasses(index, 'accounts_date')"
                >
                  {{ isEmptyMessage(formatDate(financial.accounts_date)) }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <header-cell>
                  <template slot="heading">Turnover:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.turnover"
                  :percentageValue="financial.turnover_delta_percentage"
                  :direction="financial.turnover_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'turnover')"
                />
              </tr>
              <tr>
                <header-cell>
                  <template slot="heading">Gross Profit:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.gross_profit"
                  :percentageValue="financial.gross_profit_delta_percentage"
                  :direction="financial.gross_profit_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'gross_profit')"
                />
              </tr>
              <tr>
                <header-cell>
                  <template slot="heading">Net Profit/(Loss) Before Tax:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.net_profit_before_tax"
                  :percentageValue="financial.net_profit_before_tax_delta_percentage"
                  :direction="financial.net_profit_before_tax_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'net_profit_before_tax')"
                />
              </tr>

              <tr>
                <header-cell>
                  <template slot="heading">Total Fixed Assets:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.total_fixed_assets"
                  :percentageValue="financial.total_fixed_assets_delta_percentage"
                  :direction="financial.total_fixed_assets_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'total_fixed_assets')"
                />
              </tr>
              <tr>
                <header-cell>
                  <template slot="heading">Stock:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.stock"
                  :percentageValue="financial.stock_delta_percentage"
                  :direction="financial.stock_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'stock')"
                />
              </tr>

              <tr>
                <header-cell>
                  <template slot="heading">Cash:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.cash"
                  :percentageValue="financial.cash_delta_percentage"
                  :direction="financial.cash_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'cash')"
                />
              </tr>

              <tr>
                <header-cell>
                  <template slot="heading">Employee Numbers:</template>
                </header-cell>
                <simple-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :displayValue="(financial.employee_numbers === null ? '-' : Math.trunc(financial.employee_numbers)) + ' '"
                  :percentageValue="financial.employee_numbers_delta_percentage"
                  :direction="financial.employee_numbers_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :extra-classes="extraClasses(index, 'employee_numbers')"
                />
              </tr>

              <tr>
                <header-cell>
                  <template slot="heading">Wages and Salaries:</template>
                </header-cell>
                <standard-cell
                  v-for="(financial, index) of financialReports"
                  :key="financial.id"
                  :currencySymbol="financial.currency.html_symbol"
                  :currencyValue="financial.wages_salaries"
                  :percentageValue="financial.wages_salaries_delta_percentage"
                  :direction="financial.wages_salaries_delta_direction"
                  :isCreditSafeReport="report.is_creditsafe_report"
                  :showPercentage="true"
                  :extra-classes="extraClasses(index, 'wages_salaries')"
                />
              </tr>
            </tbody>
          </table>
        </div>
        <!-- panel content end -->
      </div>
    </report-panel-collapsible>
  </div>
</template>

<script>
import toolsMixin from '@/components/reports/ToolsMixin';
import ReportPanelCollapsible from '@/components/reports/components/ReportPanelCollapsible';
import HeaderCell from '@/components/reports/panels/financials/tablecells/HeaderCell';
import StandardCell from '@/components/reports/panels/financials/tablecells/StandardCell';
import SimpleCell from '@/components/reports/panels/financials/tablecells/SimpleCell';
import financialsMixin from './FinancialsMixin';

export default {
  components: {
    ReportPanelCollapsible,
    HeaderCell,
    StandardCell,
    SimpleCell,
  },
  mixins: [financialsMixin, toolsMixin],
  props: ['report'],
};
</script>
